import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { map, Observable, of, Subscription } from 'rxjs';
import { FormData, MarketingAttribution } from 'src/app/services/data.model';
import { DataService } from 'src/app/services/data.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { NextButtonStateService } from 'src/app/services/next-button-state.service';
import { MainLayoutComponent } from 'src/app/shared/components/main-layout/main-layout.component';
import { FormGroupComponent } from 'src/app/shared/components/form-group/form-group.component';
import { NAVIGATION_MAP, PAGES } from 'src/app/shared/constants/navigation-map';
import { SOURCES_LIST } from './source-type.const';

type SOURCE = {
  unique_name: string;
  title: string;
};

@Component({
  selector: 'app-source-type',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MainLayoutComponent,
    FormGroupComponent,
  ],
  templateUrl: './source-type.component.html',
  styleUrl: './source-type.component.scss',
})
export class SourceTypeComponent implements OnInit, OnDestroy {
  sub: Subscription;
  formChangesSub: Subscription;
  validationSub: Subscription;
  sourceForm: FormGroup;
  selected: string = '';
  previouslyFilledData: {
    marketingAttribution: MarketingAttribution;
  };
  sources: SOURCE[] = SOURCES_LIST;
  otherTextfieldMaxLength = 125;
  otherSourceId: string = '876090099';
  username: string;
  nextStep = NAVIGATION_MAP[PAGES.SOURCE_TYPE].nextButton.route;
  otherProperties = {
    zipcodeValid: false,
    zipcodeGuid: '',
    marketSegmentGuid: '',
    ownershipValidated: false,
  };
  windowCount: number = 0;

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    public nextButtonState: NextButtonStateService,
    private navigationService: NavigationService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.setupSourcesList();
    if (this.dataService.id) {
      this.sub = this.dataService
        .getStepsData(this.dataService.id)
        .subscribe((data) => {
          this.prepopulateStepData(data);
          this.validationSub = this.dataService
            .propertyValidation()
            .subscribe((response) => {
              this.otherProperties.ownershipValidated =
                response.isPropertyOwnershipValidated;
            });
        });
    }
  }

  buildForm() {
    this.sourceForm = this.fb.group({
      selected: ['', Validators.required],
      other: '',
    });
    this.subscribeToFormChanges();
  }

  subscribeToFormChanges(): void {
    this.formChangesSub = this.sourceForm.valueChanges.subscribe((value) => {
      this.checkFormValidity();
    });
  }

  checkFormValidity() {
    setTimeout((_) => {
      this.nextButtonState.emitActivateNext(this.sourceForm.valid);
    });
  }

  prepopulateStepData(data: FormData) {
    const sessionData = JSON.parse(data.sessionData);
    if (
      data?.appointmentRequest?.customer &&
      data?.appointmentRequest?.marketingAttribution
    ) {
      this.username = data?.appointmentRequest?.customer.firstName;
      this.previouslyFilledData = {
        marketingAttribution: data.appointmentRequest.marketingAttribution,
      };
    }
    if (data?.appointmentRequest?.project?.windowCount) {
      this.windowCount = Number(data.appointmentRequest.project?.windowCount);
    }
    this.updateFormValues();
  }

  updateFormValues() {
    this.sourceForm.patchValue({
      selected:
        this.previouslyFilledData?.marketingAttribution?.sourceTypeEnum || '',
      other:
        this.previouslyFilledData?.marketingAttribution?.sourceTypeOtherText ||
        '',
    });
    this.selected =
      this.previouslyFilledData?.marketingAttribution?.sourceTypeEnum.toString() ||
      '';
    this.addOtherControlValidation(true);
  }

  addOtherControlValidation(add: boolean) {
    const other = this.sourceForm.get('other');
    if (add) {
      other.setValidators([
        Validators.required,
        Validators.maxLength(this.otherTextfieldMaxLength),
      ]);
    } else {
      other.clearValidators();
      other.updateValueAndValidity();
    }
  }

  setupSourcesList() {
    const other = SOURCES_LIST?.find(
      (item) => item.unique_name === this.otherSourceId
    );
    this.sources = SOURCES_LIST?.filter(
      (item) => item.unique_name !== this.otherSourceId
    ).sort(() => Math.random() - 0.5);
    if (other) {
      this.sources.push(other);
    }
  }

  onSelect(item: any): void {
    this.selected = item.unique_name;
    this.sourceForm.patchValue({ selected: this.selected });
    if (this.selected !== this.otherSourceId) {
      this.sourceForm.patchValue({ other: '' });
    }
    this.addOtherControlValidation(this.selected === this.otherSourceId);
  }

  onClickNext = (): Observable<any> => {
    if (this.sourceForm.valid) {
      return this.saveData();
    }
    this.sourceForm.markAllAsTouched();
    return of(false);
  };

  onClickBack = (): Observable<any> => {
    return this.sourceForm.valid
      ? this.saveData(NAVIGATION_MAP[PAGES.SOURCE_TYPE].backButton.route)
      : of(true);
  };

  saveData(nextPage?: string): Observable<any> {
    const stepsData = { ...this.dataService.stepsData };
    stepsData.appointmentRequest.marketingAttribution.sourceTypeEnum = Number(
      this.sourceForm.value.selected
    );
    stepsData.appointmentRequest.marketingAttribution.sourceTypeOtherText =
      this.sourceForm.value.other;
    stepsData.appointmentRequest.property.ownership.ownershipValidated =
      this.otherProperties.ownershipValidated;

    const sessionData = JSON.parse(stepsData.sessionData);
    sessionData.nextPage = nextPage || this.nextStep;
    stepsData.sessionData = JSON.stringify(sessionData);

    return this.dataService.saveStepsData(stepsData).pipe(
      map((res) => {
        this.navigationService.navigateToPath(nextPage || this.nextStep, {
          id: res.id,
        });
        return false;
      })
    );
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
    if (this.formChangesSub) this.formChangesSub.unsubscribe();
    if (this.validationSub) this.validationSub.unsubscribe();
  }
}
